import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { buildGoogleAuthUrl } from '@/lib/googleAuth';
import * as Sentry from "@sentry/react";
import {track} from '@vercel/analytics';

interface User {
  email: string;
  name: string;
  picture: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: () => void;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for token in URL hash on redirect
    const hash = window.location.hash.substring(1);
    const params = new URLSearchParams(hash);
    const accessToken = params.get('access_token');

    if (accessToken) {
      fetchUserInfo(accessToken);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else {
      // Check localStorage
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        setUser(JSON.parse(storedUser));
      }
      setLoading(false);
    }
  }, []);

  const fetchUserInfo = async (accessToken: string) => {
    try {
      const response = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      
      if (!response.ok) throw new Error('Failed to fetch user info');
      
      const userData = await response.json();
      
      if (!userData.email?.endsWith('@tipit.cz')) {
        throw new Error('Invalid email domain');
      }

      // Track login event - client-side tracking doesn't require await
      // But we wrap it in try-catch to handle any potential errors
      try {
        track('Login', {
          email: userData.email
        });
        console.log('Login event tracked for:', userData.email);
      } catch (error) {
        console.error('Failed to track login event:', error);
        // Don't throw - tracking failure shouldn't block login
      }

      const user = {
        email: userData.email,
        name: userData.name,
        picture: userData.picture,
      };

      setUser(user);
      localStorage.setItem('user', JSON.stringify(user));
      Sentry.setUser({ email: user.email, username: user.name });
    } catch (error) {
      console.error('Auth error:', error);
      signOut();
    } finally {
      setLoading(false);
    }
  };

  const signIn = () => {
    window.location.href = buildGoogleAuthUrl();
  };

  const signOut = () => {
    setUser(null);
    localStorage.removeItem('user');
  };

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signOut }}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};